import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, type EditorState, type Selection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import type { MathEditKind } from './utils';
import {
    decomposeMathNode,
    getMathEditTextBetween,
    insertMathEditText,
    isMathNodeType,
    mathEditLeafText,
    parseMathEditText,
    refreshPendingEditRange,
    tryComposePendingMath,
} from './utils';

export type MathEntrySide = 'left' | 'right';

export type PendingMathEdit = {
    from: number;
    to: number;
    kind: MathEditKind;
};

export type MathEditingState = {
    entrySide: MathEntrySide | null;
    pendingEdit: PendingMathEdit | null;
};

type MathEditingMeta = Partial<MathEditingState> & {
    selectComposedMathAt?: number;
};

export const mathEditingPluginKey = new PluginKey<MathEditingState>('mathEditing');

function selectionIsInsideRange(doc: EditorState['doc'], selection: Selection, from: number, to: number, kind: MathEditKind): boolean {
    if (from >= to || from < 0 || to > doc.content.size) {
        return false;
    }

    if (!selection.empty) {
        return selection.from >= from && selection.to <= to;
    }

    const text = getMathEditTextBetween(doc, from, to);
    if (!parseMathEditText(text, kind)) {
        return selection.from >= from && selection.from <= to;
    }

    return selection.from >= from && selection.from < to;
}

function selectionIsInsidePendingEdit(state: EditorState, pendingEdit: PendingMathEdit): boolean {
    return selectionIsInsideRange(state.doc, state.selection, pendingEdit.from, pendingEdit.to, pendingEdit.kind);
}

function isEditingDecomposedMath(state: EditorState): PendingMathEdit | null {
    const pluginState = mathEditingPluginKey.getState(state);
    if (!pluginState?.pendingEdit) {
        return null;
    }
    if (!selectionIsInsidePendingEdit(state, pluginState.pendingEdit)) {
        return null;
    }
    return pluginState.pendingEdit;
}

function resolveEntrySide(prevSelection: { from: number; to: number }, nodePos: number, nodeSize: number): MathEntrySide {
    if (prevSelection.from <= nodePos) {
        return 'left';
    }
    if (prevSelection.from >= nodePos + nodeSize) {
        return 'right';
    }
    return 'left';
}

function insertMathEditHardBreak(view: EditorView): boolean {
    if (!isEditingDecomposedMath(view.state)) {
        return false;
    }

    const hardBreak = view.state.schema.nodes.hardBreak;
    if (!hardBreak) {
        return false;
    }

    const { from, to } = view.state.selection;
    view.dispatch(view.state.tr.replaceWith(from, to, hardBreak.create()));
    return true;
}

function dispatchComposePendingEdit(view: EditorView, anchorPos?: number): boolean {
    const { state } = view;
    const pendingEdit = mathEditingPluginKey.getState(state)?.pendingEdit;
    if (!pendingEdit) {
        return false;
    }

    const tr = state.tr;
    if (tryComposePendingMath(tr, pendingEdit, anchorPos) === null) {
        tr.setMeta(mathEditingPluginKey, { pendingEdit: null });
        view.dispatch(tr);
        return false;
    }

    tr.setMeta(mathEditingPluginKey, { pendingEdit: null });
    view.dispatch(tr);
    return true;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        mathEditing: {
            decomposeMath: (options?: { pos?: number; cursorAt?: 'start' | 'end' }) => ReturnType;
            composeMath: () => ReturnType;
        };
    }
}

export const MathEditing = Extension.create({
    name: 'mathEditing',

    addCommands() {
        return {
            decomposeMath:
                options =>
                ({ editor, tr, dispatch }) => {
                    const { selection } = editor.state;
                    let pos = options?.pos;

                    if (pos === undefined) {
                        if (!(selection instanceof NodeSelection) || !isMathNodeType(selection.node.type.name)) {
                            return false;
                        }
                        pos = selection.from;
                    }

                    const pendingEdit = mathEditingPluginKey.getState(editor.state)?.pendingEdit;
                    if (pendingEdit) {
                        const anchor = selectionIsInsidePendingEdit(editor.state, pendingEdit) ? selection.from : pendingEdit.from;
                        if (tryComposePendingMath(tr, pendingEdit, anchor) === null) {
                            tr.setMeta(mathEditingPluginKey, { pendingEdit: null });
                        }
                        pos = tr.mapping.map(pos);
                    }

                    const node = tr.doc.nodeAt(pos);
                    if (!node || !isMathNodeType(node.type.name)) {
                        return false;
                    }

                    let cursorAt = options?.cursorAt;
                    if (!cursorAt) {
                        const pluginState = mathEditingPluginKey.getState(editor.state);
                        cursorAt = pluginState?.entrySide === 'right' ? 'end' : 'start';
                    }

                    const result = decomposeMathNode(tr, pos, cursorAt);
                    if (!result) {
                        return false;
                    }

                    tr.setMeta(mathEditingPluginKey, {
                        entrySide: null,
                        pendingEdit: { from: result.editFrom, to: result.editTo, kind: result.kind },
                    });

                    dispatch?.(tr.scrollIntoView());
                    return true;
                },

            composeMath:
                () =>
                ({ state, tr, dispatch }) => {
                    const pendingEdit = mathEditingPluginKey.getState(state)?.pendingEdit;
                    if (!pendingEdit) {
                        return false;
                    }

                    const mathPos = tryComposePendingMath(tr, pendingEdit, state.selection.from);
                    if (mathPos === null) {
                        tr.setMeta(mathEditingPluginKey, { pendingEdit: null });
                        dispatch?.(tr);
                        return false;
                    }

                    tr.setMeta(mathEditingPluginKey, { pendingEdit: null, selectComposedMathAt: mathPos });
                    dispatch?.(tr.scrollIntoView());
                    return true;
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            Enter: ({ editor }) => {
                if (isEditingDecomposedMath(editor.state)) {
                    return editor.commands.setHardBreak();
                }

                const { selection } = editor.state;
                if (!(selection instanceof NodeSelection) || !isMathNodeType(selection.node.type.name)) {
                    return false;
                }
                return editor.commands.decomposeMath();
            },
            'Shift-Enter': ({ editor }) => {
                if (!isEditingDecomposedMath(editor.state)) {
                    return false;
                }
                return editor.commands.setHardBreak();
            },
            Escape: ({ editor }) => {
                if (!isEditingDecomposedMath(editor.state)) {
                    return false;
                }
                return editor.commands.composeMath();
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin<MathEditingState>({
                key: mathEditingPluginKey,
                state: {
                    init: () => ({ entrySide: null, pendingEdit: null }),
                    apply(tr, value) {
                        const meta = tr.getMeta(mathEditingPluginKey) as Partial<MathEditingState> | undefined;
                        if (meta) {
                            return {
                                entrySide: meta.entrySide !== undefined ? meta.entrySide : value.entrySide,
                                pendingEdit: meta.pendingEdit !== undefined ? meta.pendingEdit : value.pendingEdit,
                            };
                        }

                        if (!value.pendingEdit) {
                            return value;
                        }

                        if (!tr.docChanged) {
                            return value;
                        }

                        return {
                            entrySide: value.entrySide,
                            pendingEdit: refreshPendingEditRange(tr.doc, value.pendingEdit, tr.mapping),
                        };
                    },
                },
                appendTransaction(transactions, oldState, newState) {
                    const oldPluginState = mathEditingPluginKey.getState(oldState);
                    const newPluginState = mathEditingPluginKey.getState(newState);
                    if (!oldPluginState || !newPluginState) {
                        return null;
                    }

                    let selectComposedMathAt: number | undefined;
                    for (const transaction of transactions) {
                        const txMeta = transaction.getMeta(mathEditingPluginKey) as MathEditingMeta | undefined;
                        if (txMeta?.selectComposedMathAt !== undefined) {
                            selectComposedMathAt = txMeta.selectComposedMathAt;
                        }
                    }

                    const { selection: newSelection } = newState;
                    const { selection: oldSelection } = oldState;
                    let meta: Partial<MathEditingState> | null = null;

                    if (
                        newSelection instanceof NodeSelection &&
                        isMathNodeType(newSelection.node.type.name) &&
                        !(oldSelection instanceof NodeSelection && oldSelection.from === newSelection.from)
                    ) {
                        meta = {
                            ...(meta ?? {}),
                            entrySide: resolveEntrySide(oldSelection, newSelection.from, newSelection.node.nodeSize),
                        };
                    }

                    const pendingEdit = newPluginState.pendingEdit;
                    const oldPendingEdit = oldPluginState.pendingEdit;
                    const wasInside = oldPendingEdit && selectionIsInsidePendingEdit(oldState, oldPendingEdit);
                    const isOutside = pendingEdit && !selectionIsInsidePendingEdit(newState, pendingEdit);

                    let tr = null as ReturnType<typeof newState.tr.setMeta> | null;

                    if (pendingEdit && wasInside && isOutside) {
                        tr = newState.tr;
                        const anchor = selectionIsInsidePendingEdit(oldState, pendingEdit) ? oldSelection.from : pendingEdit.from;

                        if (tryComposePendingMath(tr, pendingEdit, anchor) !== null) {
                            tr.setMeta(mathEditingPluginKey, { pendingEdit: null });
                        } else {
                            meta = { ...(meta ?? {}), pendingEdit: null };
                            tr = null;
                        }
                    }

                    if (!tr && meta) {
                        tr = newState.tr;
                        tr.setMeta(mathEditingPluginKey, meta);
                    }

                    if (selectComposedMathAt !== undefined) {
                        const selectionTr = (tr ?? newState.tr).setSelection(
                            NodeSelection.create((tr ?? newState.tr).doc, selectComposedMathAt),
                        );
                        if (tr) {
                            selectionTr.setMeta(mathEditingPluginKey, tr.getMeta(mathEditingPluginKey));
                        }
                        return selectionTr;
                    }

                    return tr;
                },
                props: {
                    handleKeyDown(view, event) {
                        if (event.key !== 'Enter' || event.ctrlKey || event.metaKey || event.altKey) {
                            return false;
                        }
                        return insertMathEditHardBreak(view);
                    },
                    handleTextInput(view, from, to, text) {
                        if (!isEditingDecomposedMath(view.state)) {
                            return false;
                        }
                        view.dispatch(insertMathEditText(view.state.tr, from, to, text));
                        return true;
                    },
                    handlePaste(view, _event, slice) {
                        if (!isEditingDecomposedMath(view.state)) {
                            return false;
                        }
                        const text = slice.content.textBetween(0, slice.content.size, undefined, mathEditLeafText);
                        if (!text) {
                            return false;
                        }
                        const { from, to } = view.state.selection;
                        view.dispatch(insertMathEditText(view.state.tr, from, to, text));
                        return true;
                    },
                },
                view(view) {
                    const composeOnBlur = () => {
                        window.setTimeout(() => {
                            if (view.isDestroyed || view.hasFocus()) {
                                return;
                            }
                            dispatchComposePendingEdit(view);
                        }, 0);
                    };

                    view.dom.addEventListener('blur', composeOnBlur);

                    return {
                        destroy() {
                            view.dom.removeEventListener('blur', composeOnBlur);
                        },
                    };
                },
            }),
        ];
    },
});

export default MathEditing;
